import { useState, useEffect } from 'react';
import axios from '../utils/axiosInstance';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Trash2, Camera } from 'lucide-react';
import DeleteAccountDialog from '../components/DeleteAccountDialog';
import ImageUploader from '../components/ImageUploader';

interface ProfilePageProps {
  onLogout: () => void;
  onUserUpdated?: (updates: { fullName?: string; profilePicture?: string }) => void;
}

export default function ProfilePage({ onLogout, onUserUpdated }: ProfilePageProps) {
  const [activeTab, setActiveTab] = useState<'personal' | 'password'>('personal');
  const [profile, setProfile] = useState({ fullName: '', email: '', username: '', domain: '', createdAt: '', profilePicture: '' });
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get('/api/account/profile');
      setProfile(res.data);
      setFullName(res.data.fullName || '');
    } catch {
      setProfileMsg({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUploaded = async (url: string) => {
    setAvatarMsg(null);
    setAvatarSaving(true);
    try {
      const res = await axios.put('/api/account/profile', { profilePicture: url });
      setProfile(prev => ({ ...prev, profilePicture: res.data.profilePicture }));
      onUserUpdated?.({ profilePicture: res.data.profilePicture });
      setAvatarMsg({ type: 'success', text: 'Profile picture updated' });
    } catch (err: any) {
      setAvatarMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update profile picture' });
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarMsg(null);
    setAvatarSaving(true);
    try {
      const res = await axios.put('/api/account/profile', { profilePicture: '' });
      setProfile(prev => ({ ...prev, profilePicture: res.data.profilePicture }));
      onUserUpdated?.({ profilePicture: '' });
      setAvatarMsg({ type: 'success', text: 'Profile picture removed' });
    } catch (err: any) {
      setAvatarMsg({ type: 'error', text: err.response?.data?.message || 'Failed to remove profile picture' });
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      const res = await axios.put('/api/account/profile', { fullName: fullName.trim() });
      setProfile(prev => ({ ...prev, fullName: res.data.fullName }));
      onUserUpdated?.({ fullName: res.data.fullName });
      setProfileMsg({ type: 'success', text: 'Profile updated' });
    } catch (err: any) {
      setProfileMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);

    if (newPassword.length < 6) {
      setPwMsg({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setChangingPw(true);
    try {
      const res = await axios.put('/api/account/change-password', { oldPassword, newPassword });
      if (res.data.forceLogout) {
        onLogout();
        return;
      }
    } catch (err: any) {
      setPwMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change password' });
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading profile..." />;
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="page-title">Profile</h2>
      </div>

      <Tabs value={activeTab} onValueChange={(v: string) => setActiveTab(v as 'personal' | 'password')}>
        <TabsList>
          <TabsTrigger value="personal">Personal Information</TabsTrigger>
          <TabsTrigger value="password">Change Password</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-4">
          <div className="space-y-6 max-w-2xl">
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              <div className="flex items-center gap-5">
                <ImageUploader
                  onImageUploaded={handleAvatarUploaded}
                  aspectRatio={1}
                  maxSizeMB={2}
                  circularCrop
                  className="shrink-0"
                >
                  <div className="group relative w-20 h-20" title="Change profile picture">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center text-primary text-2xl font-semibold ring-2 ring-border">
                      {profile.profilePicture ? (
                        <img src={profile.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        (profile.fullName || profile.username)?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0.5 text-white">
                      <Camera className="size-5" />
                      <span className="text-[10px] font-medium">
                        {avatarSaving ? 'Saving...' : profile.profilePicture ? 'Change' : 'Upload'}
                      </span>
                    </div>
                  </div>
                </ImageUploader>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">
                    {profile.fullName || profile.username}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click the photo to change it. PNG or JPEG, max 2MB.
                  </p>
                  {profile.profilePicture && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2 -ml-2 text-muted-foreground hover:text-danger"
                      disabled={avatarSaving}
                      onClick={handleRemoveAvatar}
                    >
                      <Trash2 className="size-3.5" />
                      Remove
                    </Button>
                  )}
                  {avatarMsg && (
                    <p className={`text-sm mt-2 ${avatarMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {avatarMsg.text}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              {/* <h3 className="text-section-title mb-4">Personal Information</h3> */}
              <form onSubmit={handleSaveProfile} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    maxLength={100}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={profile.email || '—'} disabled className="bg-muted" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" value={profile.username} disabled className="bg-muted" />
                </div>
                {profile.domain && (
                  <div className="space-y-1.5">
                    <Label htmlFor="domain">Domain</Label>
                    <Input id="domain" value={profile.domain} disabled className="bg-muted" />
                  </div>
                )}
                {profileMsg && (
                  <p className={`text-sm ${profileMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {profileMsg.text}
                  </p>
                )}
                <Button type="submit" disabled={saving || fullName.trim() === profile.fullName}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </div>

            {/* Danger Zone */}
            <div className="bg-card rounded-lg shadow p-4 sm:p-6 border border-destructive/40">
              <h3 className="text-base font-semibold text-destructive mb-3">Delete Account</h3>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 max-w-md">
                  {/* <div className="text-sm font-medium mb-1">Delete my account</div> */}
                  <p className="text-xs text-muted-foreground">
                    Takes your website offline immediately and permanently erases your account
                    and content after a 30-day grace period. Billing records are retained as
                    required by law.
                  </p>
                </div>
                <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete your account
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="password" className="mt-4">
          <div className="space-y-6 max-w-2xl">
            <div className="bg-card rounded-lg shadow p-4 sm:p-6">
              {/* <h3 className="text-section-title mb-4">Change Password</h3> */}
              <form onSubmit={handleChangePassword} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="oldPassword">Current Password</Label>
                  <PasswordInput
                    id="oldPassword"
                    value={oldPassword}
                    onChange={e => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword">New Password</Label>
                  <PasswordInput
                    id="newPassword"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 chars)"
                    required
                    minLength={8}
                    // ✅ Enable the new features here:
    showStrengthMeter 
    showCopyBtn
    showRequirements
    // showGenerateBtn // (Optional) Uncomment if you want the shuffle/dice icon
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <PasswordInput
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password (min 8 chars)"
                    required
                    minLength={8}
                  />
                </div>
                {pwMsg && (
                  <p className={`text-sm ${pwMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {pwMsg.text}
                  </p>
                )}
                <Button type="submit" variant="primary" disabled={changingPw || !oldPassword || !newPassword || !confirmPassword}>
                  {changingPw ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <DeleteAccountDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        username={profile.username}
        onDeleted={onLogout}
      />
    </div>
  );
}
